import { LeftNavigation } from "../../components/v2/explore/LeftNavigation";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { SearchBox } from "../../components/v2/explore/SearchBox";
import { PageContainer } from "../../components/v2/PageContainer";
import { BrowseVideoContainer } from "../../components/v2/explore/BrowseVideoContainer";
import { useHistory, useLocation } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../context/UserContext";
import { CourseStructureContext } from "../../context/CourseStructureContext";
import { AllCourses, CourseStructureCtx, SkillDetails, UserCtx } from "../../interfaces";
import { VideoListContainer } from "../../components/v2/explore/VideoListContainer";
import axios from "../../services/axios";
import { SkeletonLoader } from "../../components/v2/explore/SkeletonLoader";
import { ExplorePageType, Role } from "../../enums";
import { generateGtmEvent, HandleDataLayer } from "../../Helpers";
import { ExploreHomeHeader } from "../../components/v2/explore/Header";
import { ModuleActivity } from "../../components/v3/explore/ModuleActivity";
import { Helmet } from "react-helmet-async";

const getHeight = () =>
  document.body.scrollHeight - document.documentElement.clientHeight;

const getScrollY = () => window.scrollY;

const SingleCourseCard = ({ course }: { course: AllCourses }) => {
  const history = useHistory();
  return (
    <Box
      onClick={() => {
        HandleDataLayer(
          "link",
          "click",
          "internal link",
          "main content",
          "explore",
          `${course.name}`,
          "onclick"
        );
        history.push({
          pathname: "/explore/activities",
          search: `?courseId=${course.id}`,
        });
      }}
      sx={{
        border: "2px solid #DEDEDE",
        borderRadius: "16px",
        height: "100%",
        width: "276px",
        transition: "box-shadow 200ms ease-in-out",
        "&:hover": {
          boxShadow: "0px 8px 12px #00000029",
          cursor: "pointer",
        },
      }}>
      <Box
        sx={{
          backgroundColor: `${course.metadata.color}`,
          width: "100%",
          height: "124.8px",
          border: "1px solid #DEDEDE",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}>
        <img
          src={`${course.metadata.icon}`}
          alt="course thumbnail"
          style={{
            width: "60%",
            height: "60%",
          }}
        />
      </Box>
      <Box sx={{ height: "auto", padding: "16px" }}>
        <Typography
          variant={"h6"}
          sx={{
            textAlign: "left",
            color: "#191919",
            font: "normal normal bold 24px/36px proxima-soft",
          }}>
          {course.name}
        </Typography>
      </Box>
    </Box>
  );
};

export const ExplorePage = () => {
  const [windowHeight, setWindowHeight] = useState(getHeight());
  const [scrollY, setScrollY] = useState(getScrollY());
  const [reachedBottom, setReachedBottom] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const resizeListener = () => {
      setWindowHeight(getHeight());
    };
    window.addEventListener("resize", resizeListener);
    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, []);

  useEffect(() => {
    const scrollListener = () => {
      setScrollY(getScrollY());
    };
    window.addEventListener("scroll", scrollListener);
    return () => {
      window.removeEventListener("scroll", scrollListener);
    };
  }, []);

  useEffect(() => {
    if (windowHeight - scrollY < 5) {
      let data = setTimeout(() => {
        setReachedBottom(true);
      }, 500);
      return () => clearTimeout(data);
    }
  }, [windowHeight, scrollY]);

  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const currentCourse = courseStructureCtx.courseTreeStructure?.getCurrentCourse();
  const allCourses = courseStructureCtx.allCourses;
  const userCtx = useContext(UserContext) as UserCtx;
  let location = useLocation();
  const history = useHistory();
  let isBrowsePage = location.pathname === "/explore/browse";
  let isSavedPage = location.pathname === "/explore/saved";
  let isSearchPage = location.pathname === "/explore/search";
  let isFilterPage = location.pathname === "/explore/filter";
  let isModulePage = location.pathname === "/explore/module";
  let isActivityPage = location.pathname === "/explore/activities";
  // get query params
  let queryParams = new URLSearchParams(location.search);
  const searchQuery = queryParams.get("search") as string;
  const filterQuery = queryParams.get("filter") as string;
  const moduleQuery = queryParams.get("moduleId") as string;
  const courseId = queryParams.get("courseId") as string;
  const [searchInputText, setSearchInputText] = useState<string | undefined>(
    searchQuery ? searchQuery : undefined
  );
  const [filterInputText, setFilterInputText] = useState<string | undefined>(
    filterQuery ? filterQuery : undefined
  );

  const [selectedModule, setSelectedModule] = useState<string>(
    moduleQuery ? moduleQuery : ""
  );

  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);

  const [filterChips, setFilterChips] = useState<any[]>([]);

  const [videoListResults, setVideoListResults] = useState<SkillDetails[]>([]);

  const [showCourseSelector, setShowCourseSelector] = useState<boolean>(false);

  useEffect(() => {
    if (!courseId) {
      setShowCourseSelector(true);
    } else {
      setShowCourseSelector(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (userCtx.dbUser.role === Role.student && userCtx.studentCurrentClass) {
      history.push({
        pathname: "/explore/browse",
        search: `?courseId=${userCtx.studentCurrentClass?.course?.id}`,
      });
    }
  }, [
    userCtx.dbUser,
    history,
    userCtx.studentCurrentClass,
    userCtx.studentCurrentClass?.course?.id,
  ]);

  const onSubmitSearch = (submittedSearchQuery: string | undefined) => {
    setSearchInputText(submittedSearchQuery);
    //setVideoListResults([]);
    setCurrentPage(1);
    setTotalPages(1);
    // if (isSavedPage) {
    //   return;
    // }
    applySearchAndFilter(submittedSearchQuery, filterInputText);
  };
  const onSubmitFilter = (submittedFilter: string | undefined) => {
    setFilterInputText(submittedFilter);
    applySearchAndFilter(searchInputText, submittedFilter);
  };

  const applySearchAndFilter = (
    search: string | undefined,
    filter: string | undefined
  ) => {
    if ((!search || search.length === 0) && (!filter || filter.length === 0)) {
      history.push(`/explore/browse?courseId=${courseId}`);
      return;
    }
    if (search && search.length > 0 && (!filter || filter.length === 0)) {
      history.push(`/explore/search?courseId=${courseId}&search=${search}`);
      return;
    }
    if ((!search || search.length === 0) && filter && filter.length > 0) {
      history.push(`/explore/filter?courseId=${courseId}&filter=${filter}`);
      return;
    }
    if (search && search.length > 0 && filter && filter.length > 0) {
      history.push(
        `/explore/search?courseId=${courseId}&search=${search}&filter=${filter}`
      );
      return;
    }
  };

  // commented out because they said to remove filter functionality will be usefull when they comeback with filter functionality
  // useEffect(() => {
  //   if (isFilterPage) {
  //     setFilterChips([]);
  //     if (filterInputText && filterInputText.length > 0) {
  //       GetSkillsBySearchAndFilter(undefined, filterQuery).then((response) => {
  //         setVideoListResults(response.skills);
  //       });
  //       filterInputText.split(",").forEach((filter) => {
  //         setFilterChips((prev) => {
  //           return [
  //             ...prev,
  //             courseStructureCtx.moduleStrings.find(
  //               (moduleString) => moduleString.id === filter
  //             ),
  //           ];
  //         });
  //       });
  //     } else {
  //       setFilterChips([]);
  //       handleFilterRemove("all");
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [
  //   filterInputText,
  //   isFilterPage,
  //   filterQuery,
  //   courseStructureCtx.moduleStrings,
  // ]);

  // useEffect(() => {
  //   if (isSearchPage) {
  //     setFilterInputText("");
  //     setFilterChips([]);
  //     if (searchInputText && searchInputText.length > 0) {
  //       GetSkillsBySearchAndFilter(searchInputText, undefined).then(
  //         (response) => {
  //           setVideoListResults(response.skills);
  //         }
  //       );
  //     }
  //   }
  // }, [searchInputText, isSearchPage]);

  useEffect(() => {
    if (isSearchPage && reachedBottom && currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reachedBottom]);

  useEffect(() => {
    const GetSkillsBySearchAndFilter = async (
      searchQuery?: string | undefined,
      filterQuery?: string | undefined,
      page?: number | 1
    ) => {
      try {
        let response = await axios.get(`/skills`, {
          params: {
            courseId: courseId,
            searchQuery,
            filterQuery,
            page: currentPage,
          },
        });
        return response.data;
      } catch (e) {
        console.error("Error getting skills by search and filter: ", e);
        throw e;
      }
    };

    if (isSearchPage || isFilterPage) {
      if (currentPage < 2) {
        setIsDataLoading(true);
      } else {
      }
      setFilterChips([]);
      if (filterInputText && filterInputText.length > 0) {
        // rewrite the explore filter based on new tree structure
        // filterInputText.split(",").forEach((filter) => {
        //   setFilterChips((prev) => {
        //     return [
        //       ...prev,
        //       courseStructureCtx.moduleStrings.find(
        //         (moduleString) => moduleString.id === filter
        //       ),
        //     ];
        //   });
        // });
      } else {
        setFilterChips([]);
        setFilterInputText(undefined);
      }
      if (searchInputText && searchInputText.length > 0) {
        setSearchInputText(searchInputText);
      } else {
        setSearchInputText(undefined);
      }
      const getData = setTimeout(() => {
        GetSkillsBySearchAndFilter(searchInputText, filterInputText).then((response) => {
          setVideoListResults((prevState) => {
            return [...prevState, ...response.skills];
          });
          setIsDataLoading(false);
          setReachedBottom(false);
          setTotalPages(response.totalPages);
          setWindowHeight(getHeight());
        });
      }, 1000);
      return () => clearTimeout(getData);
    }
  }, [
    searchInputText,
    filterInputText,
    isSearchPage,
    isFilterPage,
    // courseStructureCtx.moduleStrings,
    currentPage,
    courseId,
  ]);
  // Commented out because client don't want search to work individually for each page and section this function will be usefull if they want search to work on sved documents only
  // useEffect(() => {
  //   if (isSavedPage && searchInputText) {
  //     let savedListSearch = videoListResults.filter((video) =>
  //       video.title.toLowerCase().includes(searchInputText.toLowerCase())
  //     );
  //     console.log("savedListSearch: ", savedListSearch);
  //     setVideoListResults(savedListSearch);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [searchInputText]);

  useEffect(() => {
    const GetSkillDetails = async (skillIds: string[]) => {
      try {
        return await userCtx.getSkillDetailsBySkillIds(skillIds);
      } catch (e) {
        console.error("Error getting skill details: ", e);
        throw e;
      }
    };

    if (isBrowsePage) {
      setSelectedModule("");
      setFilterInputText("");
      setSearchInputText("");
      setFilterChips([]);
      setIsDataLoading(false);
      setVideoListResults([]);
    }
    if (isSavedPage) {
      setVideoListResults([]);
      setIsDataLoading(true);
      setSelectedModule("");
      setFilterInputText("");
      setSearchInputText("");
      setFilterChips([]);
      GetSkillDetails(userCtx.userSavedSkills)
        .then((r) => {
          setVideoListResults(r);
          setIsDataLoading(false);
        })
        .catch((e) => console.error("Error getting skill details: ", e));
    }
    if (isModulePage) {
      setIsDataLoading(true);
      let currentModule = courseStructureCtx.courseTreeStructure?.getModule(
        courseStructureCtx.currentModuleId
      );
      setSelectedModule(currentModule?.getFormattedModuleName()!);
      setFilterInputText("");
      setSearchInputText("");
      setFilterChips([]);
      let ski = currentModule?.getAllSkillIds();
      if (!ski) {
        console.error("Error getting skill ids from module: ", currentModule);
        return;
      }
      GetSkillDetails(ski)
        .then((r) => {
          setVideoListResults(r);
          setIsDataLoading(false);
        })
        .catch((e) => console.error("Error getting skill details: ", e));
      // let selectedModuleSkill = courseStructureCtx?.courseStructure?.targets.reduce(
      //   (acc: SkillDetails[], target) => {
      //     target.skills.map(function (skill) {
      //       if (acc.find((s) => s.skill_id === skill.skill_id)) {
      //         return null;
      //       }
      //       //acc.find((s) => s.id === skill.id) || acc.push(skill);
      //       acc.push(skill as SkillDetails);
      //       return null;
      //     });
      //     return acc;
      //   },
      //   [] as SkillDetails[]
      // );
      // if (selectedModuleSkill) {
      //   setVideoListResults(selectedModuleSkill);
      // }
    }
  }, [
    isBrowsePage,
    isSavedPage,
    isModulePage,
    userCtx.userSavedSkills,
    courseStructureCtx.currentModuleId,
    userCtx,
    searchInputText,
    courseStructureCtx.courseTreeStructure,
  ]);

  const handleFilterRemove = (filter: string) => {
    if (filter === "all") {
      setFilterChips([]);
      setFilterInputText(undefined);
      //history.push(`/explore`);
      applySearchAndFilter(searchInputText, undefined);
    } else {
      let newFilterChips = filterChips.filter((chip) => chip.id !== filter);
      setFilterChips(newFilterChips);
      let newFilterString = newFilterChips.map((chip) => chip.id).join(",");
      setFilterInputText(newFilterString);
      applySearchAndFilter(searchInputText, newFilterString);
    }
  };

  let onSearchInputChange = (e: any) => {
    onSubmitSearch(e.target.value);
    generateGtmEvent({
      event: "search",
      action: "type",
      name: "onenter",
      type: "search keywords",
      region: "main content",
      section: "explore",
      text: e.target.value,
    });
  };

  return (
    <>
      <Helmet>
        <title>
          {userCtx.dbUser.role === Role.student && currentCourse
            ? "Explore"
            : currentCourse?.name!}{" "}
          | Digit
        </title>
      </Helmet>
      {!showCourseSelector && (
        <>
          <ExploreHomeHeader
            title={
              userCtx.dbUser.role === Role.student && currentCourse
                ? "Explore"
                : currentCourse?.name!
            }
          />
          <PageContainer>
            <Box sx={{ display: "flex" }} gap={10}>
              <Box sx={{ width: "276px" }}>
                <LeftNavigation />
              </Box>
              <Box sx={{ width: "calc(100% - 276px)", marginBottom: "20px" }}>
                {!isActivityPage && (
                  <SearchBox
                    hideFilterButton={true}
                    searchText={
                      searchInputText && searchInputText.length > 0 ? searchInputText : ""
                    }
                    onSubmit={onSubmitSearch}
                    onInputChange={onSearchInputChange}
                    activeFilters={
                      filterInputText && filterInputText.length > 0
                        ? (filterInputText.split(",") as string[])
                        : []
                    }
                    // availableFilters={courseStructureCtx.moduleStrings}
                    availableFilters={[]}
                    onFilterSubmit={onSubmitFilter}
                  />
                )}
                {isDataLoading && <SkeletonLoader loaderType={"list"} />}
                {isActivityPage && <ModuleActivity />}
                {isBrowsePage && <BrowseVideoContainer />}
                {isSavedPage && !isDataLoading && (
                  <VideoListContainer
                    videoListTitle={"Saved videos"}
                    videoList={videoListResults}
                    pageType={ExplorePageType.SAVED_PAGE}
                  />
                )}
                {isSearchPage && !isDataLoading && searchInputText && (
                  <VideoListContainer
                    videoListTitle={`Search results for “${searchInputText}”`}
                    onFilterRemove={handleFilterRemove}
                    filterChips={filterChips}
                    videoList={videoListResults}
                    pageType={ExplorePageType.SEARCH_PAGE}
                  />
                )}
                {isFilterPage && filterInputText && (
                  <VideoListContainer
                    onFilterRemove={handleFilterRemove}
                    filterChips={filterChips}
                    videoList={videoListResults}
                    pageType={ExplorePageType.FILTER_PAGE}
                  />
                )}
                {isModulePage && selectedModule && !isDataLoading && (
                  <VideoListContainer
                    videoListTitle={selectedModule}
                    videoList={videoListResults}
                    pageType={ExplorePageType.MODULE_PAGE}
                  />
                )}
                {videoListResults.length !== 0 &&
                  isSearchPage &&
                  reachedBottom &&
                  totalPages - currentPage !== 0 && (
                    <CircularProgress
                      sx={{
                        position: "fixed",
                        bottom: "20px",
                        left: "50%",
                      }}
                    />
                  )}
              </Box>
            </Box>
          </PageContainer>
        </>
      )}
      {showCourseSelector && (
        <>
          <Helmet>
            <title>Explore | Digit</title>
          </Helmet>
          <PageContainer>
            <Typography
              variant={"h1"}
              sx={{
                textAlign: "left",
                color: "#191919",
                font: "normal normal bold 40px/36px proxima-soft",
                marginBottom: "32px",
              }}>
              Explore
            </Typography>
            <Grid container spacing={{ xs: 2, md: 3 }} rowGap={"30px"}>
              {allCourses.map((course, index) => {
                return (
                  <Grid item xs={12} sm={8} md={6} lg={3} xl={3} key={index}>
                    <SingleCourseCard course={course} />
                  </Grid>
                );
              })}
            </Grid>
          </PageContainer>
        </>
      )}
    </>
  );
};
